import * as Yup from 'yup'
import { cloneDeep, get, isEmpty, isEqual, toArray, toNumber } from 'lodash'
import { v4 as uuid } from 'uuid'

import { findStandardModel } from '@/components/forms/formik/selects/modelGating'
import { getStringValueFromBoolean } from '@/utils/helpers'

export const criteriaTypeOptions = [
  { value: 'ai', label: 'AI' },
  { value: 'manual', label: 'Manual' },
]

export const criteriaValidationSchema = Yup.object().shape({
  name: Yup.string()
    .max(500, 'Criteria name must be less than five hundred characters')
    .required('Name is required'),
  description: Yup.string(),
  ai_shell_prompt: Yup.string(),
  ai_prompt: Yup.string(),
  ai_secondary_prompt: Yup.string(),
  criteria_type: Yup.string().required(),
})

// A prompt's model is `llm_model_id` and nothing else: with it unset the prompt
// API falls back to the legacy `model` name, which these forms no longer send, so
// the save comes back as an unexplained 500. Required only when this save
// actually writes a prompt — a user without prompt-edit rights never POSTs one,
// and never loads the model catalog, so there'd be nothing to pick.
const requiredLlmModelId = (schema) =>
  schema.typeError('AI Model is required').required('AI Model is required')

// A cleared Select writes '' (see components/forms/formik/Select.js), which a bare
// Yup.number() casts to NaN and rejects with a type error — including where the model is
// optional, which would block a save the rule means to allow. Normalize it to "unset" the
// way the other numeric fields here do.
const llmModelIdSchema = () =>
  Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))

const singleEmailSchema = Yup.string().email()

// `email_on_failure` is stored as a comma-separated string so existing single-address rows stay valid.
// Split it into trimmed, non-empty, case-insensitively unique recipients (first spelling wins).
export const splitEmailOnFailure = (value) => {
  if (!value) return []
  const seen = new Set()
  return value
    .split(',')
    .map((address) => address.trim())
    .filter((address) => {
      const key = address.toLowerCase()
      if (!address || seen.has(key)) return false
      seen.add(key)
      return true
    })
}

// Tells the user exactly what to correct. Returns null when every entry is a valid address.
export const emailOnFailureErrorMessage = (value) => {
  const invalid = splitEmailOnFailure(value).filter(
    (address) => !singleEmailSchema.isValidSync(address)
  )
  if (isEmpty(invalid)) return null
  // A single "entry" holding a semicolon or whitespace almost always means the wrong separator was used.
  if (invalid.some((address) => /[;\s]/.test(address))) {
    return 'Put a comma between each address: name@company.com,other@company.com'
  }
  return `Fix or remove these email addresses: ${invalid.join(', ')}`
}

// Canonical form for saving: plain comma-joined list, or null when there are no recipients.
export const normalizeEmailOnFailure = (value) => {
  const recipients = splitEmailOnFailure(value)
  return isEmpty(recipients) ? null : recipients.join(',')
}

export const getCriteriaPromptValidationSchema = ({ canEditPrompts = false } = {}) =>
  Yup.object().shape({
    llm_model_id: llmModelIdSchema().when(['criteria_type', 'manual_timestamp_markers'], {
      // Mirrors `promptsEnabled` in QACopilotScorecardCriteriaPrompt — the same
      // condition that renders the model dropdown.
      is: (criteriaType, manualTimestampMarkers) =>
        canEditPrompts && (criteriaType === 'ai' || manualTimestampMarkers === 'true'),
      then: requiredLlmModelId,
      otherwise: (schema) => schema.notRequired(),
    }),
    name: Yup.string()
      .max(500, 'Criteria name must be less than five hundred characters')
      .required('Name is required'),
    description: Yup.string(),
    criteria_type: Yup.string().required(),
    user_prompt: Yup.string().when('criteria_type', {
      is: (val) => val === 'ai',
      then: (schema) =>
        schema
          .matches(/\$transcript/, "AI prompt must include '$transcript'")
          .required('Prompt is required for AI scoring'),
      otherwise: (schema) => schema.notRequired(),
    }),
    must_be_present: Yup.string('Passing answer is required').when('criteria_type', {
      is: (val) => val === 'ai',
      then: (schema) =>
        schema
          .oneOf(['true', 'false'], 'Must be "yes" or "no"')
          .required('Passing answer is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    silence_threshold_seconds: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(5, 'Silence threshold must be at least 5 seconds'),
    email_on_failure: Yup.string()
      .nullable()
      .test('email-on-failure-list', function validateEmailOnFailure(value) {
        const message = emailOnFailureErrorMessage(value)
        return message ? this.createError({ message }) : true
      }),
  })

export const criteriaPointsValidationSchema = Yup.object().shape({
  sections: Yup.array().of(
    Yup.object().shape({
      criteria: Yup.array().of(
        Yup.object().shape({
          weight: Yup.number()
            .integer('Points must be a whole number')
            .positive('Points must be a positive number')
            .min(1, 'Points must be greater than 0')
            .max(100, 'Points must be less than or equal to 100')
            .required('Points are required'),
        })
      ),
    })
  ),
})

export const getSettingsValidationSchema = ({ isPromptEditor = false } = {}) =>
  Yup.object().shape({
    // The eligibility prompt is only written when a prompt editor leaves prompt
    // text in the form (see handleSaveSettings) — that's exactly when it needs a
    // model. See `requiredLlmModelId`.
    llm_model_id: llmModelIdSchema().when('user_prompt', {
      is: (userPrompt) => isPromptEditor && !isEmpty(userPrompt),
      then: requiredLlmModelId,
      otherwise: (schema) => schema.notRequired(),
    }),
    should_eligibility_respond_yes: Yup.string('Expected response cannot be blank')
      .oneOf(['true', 'false'], 'Must be "yes" or "no"')
      .required('Expected response cannot be blank'),
    silence_threshold_seconds: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(5, 'Silence threshold must be at least 5 seconds'),
    exceeds_threshold: Yup.number()
      .required('"Exceeds" is required')
      .moreThan(Yup.ref('meets_threshold'), 'Cannot be less than "Meets"')
      .max(100, 'Cannot be more than 100'),
    meets_threshold: Yup.number()
      .required('"Meets" is required')
      .moreThan(Yup.ref('improvement_threshold'), 'Cannot be less than "Below"')
      .lessThan(Yup.ref('exceeds_threshold'), 'Cannot be more than "Exceeds"'),
    improvement_threshold: Yup.number()
      .required('"Below" is required')
      .lessThan(Yup.ref('exceeds_threshold'), 'Cannot be more than "Exceeds"')
      .lessThan(Yup.ref('meets_threshold'), 'Cannot be more than "Meets"')
      .min(0, 'Cannot be less than 0'),
    min_duration_in_minutes: Yup.number()
      .min(1, 'Must be at least 1 minute')
      .max(120, 'Cannot be more than 120 minutes')
      .required('Minimum call length is required'),
    max_duration_in_minutes: Yup.number()
      .min(0, 'Cannot be less than 0.')
      .moreThan(Yup.ref('min_duration_in_minutes'), 'Max must be greater than min'),
    min_message_count: Yup.number().nullable().min(0, 'Cannot be less than 0.'),
    max_message_count: Yup.number()
      .nullable()
      .min(0, 'Cannot be less than 0.')
      .test(
        'max-greater-than-min',
        'Max must be greater than min',
        function maxMessageCountCheck(value) {
          const min = this.parent.min_message_count
          if (min == null || value == null) return true
          return value > min
        }
      ),

    scorecard_frequency: Yup.string().oneOf(['weekly', 'monthly'], 'Must be "weekly" or "monthly"'),
    required_score_count: Yup.number()
      .typeError('Must be at least 1')
      .min(1, 'Must be at least 1')
      .max(9999, 'Cannot be more than 9999'),
    monthly_cutoff_day: Yup.number().when(['distribute_evenly', 'scorecard_frequency'], {
      is: (distribute_evenly, scorecard_frequency) =>
        distribute_evenly === true && scorecard_frequency === 'monthly',
      then: Yup.number()
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .integer('Must be a whole number')
        .min(1, 'Must be between 1 and 31')
        .max(31, 'Must be between 1 and 31'),
      otherwise: Yup.number()
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value)),
    }),
    required_manual_task_count: Yup.number()
      .nullable()
      .transform(function transformEmptyStringToNull(value, originalValue) {
        return originalValue === '' ? null : value
      })
      .min(0, 'Cannot be less than 0')
      .max(9999, 'Cannot be more than 9999')
      .test(
        'manual-task-count-lte-score-count',
        'Cannot be greater than Scoring Goal',
        function manualTaskCountLteScoreCount(value) {
          const { required_score_count } = this.parent
          // Only validate if both values are not null/undefined
          if (value == null || required_score_count == null) {
            return true
          }
          return value <= required_score_count
        }
      ),
    organization_id: Yup.string().required('Organization is required'),
    user_prompt: Yup.string()
      .nullable()
      .matches(/\$transcript/, "AI prompt must include '$transcript'"),
  })

export const addTemporaryCriteriaHelper = (section) => {
  return {
    scid: uuid(),
    name: 'New Criteria',
    description: '',
    criteria_type: 'ai',
    order_id: get(section, 'measures[0].criteria.length') || 0,
    ssid: section.ssid,
    is_new: true,
  }
}

export const addDefaultSectionHelper = () => {
  return {
    ssid: uuid(),
    name: 'Default Section',
    measures: [{ name: 'Default Measure', criteria: [] }],
    order_id: 0,
  }
}

export const getCriteriaPointsInitialValues = (scorecardConfig) => {
  if (isEmpty(scorecardConfig?.sections)) {
    return { sections: [] }
  }

  const intitialCriteriaPointsValues = scorecardConfig.sections.map((section) => ({
    ...section,
    criteria: toArray(section.measures.find((measure, mIndex) => mIndex === 0)?.criteria).map(
      (criteria, cIndex) => ({
        ...criteria,
        order_id: cIndex,
        weight: criteria.weight || 1,
      })
    ),
  }))

  return { sections: intitialCriteriaPointsValues }
}

export const getTotalPoints = (values) => {
  return toNumber(
    values.sections.reduce((acc, section) => {
      return (
        section.criteria.reduce((acc2, criteria) => {
          return acc2 + toNumber(criteria.weight)
        }, 0) + acc
      )
    }, 0)
  )
}

export const getFilteredCriteria = ({ is_new, ssid, ...criteria }) => criteria

const DEFAULT_FALLBACK_MODEL_NAMES = ['gpt4o-mini', 'gpt-4o-mini']

// Never seeds a deprecated model, as `getDefaultQaCopilotModelId` doesn't: the fallback dropdown hides deprecated
// options on a new scorecard, so defaulting to one would preselect a value the user can't see to change, and
// turning the fallback strategy on would then block the save.
export const getDefaultFallbackModelId = (modelOptions = []) => {
  const preferredModel = modelOptions.find(
    (option) => DEFAULT_FALLBACK_MODEL_NAMES.includes(option.name) && !option.deprecated
  )
  return preferredModel?.value || modelOptions.find((option) => !option.deprecated)?.value || null
}

// New QA Copilot prompts (criteria, eligibility, guided setup, auto-populate) default to the
// standard model (STANDARD_MODEL) so nothing starts on a phased-out one; the optimal default is
// still TBD. Fall back to the first non-deprecated option (never a deprecated one) if the
// standard model isn't in the catalog.
export const getDefaultQaCopilotModelId = (modelOptions = []) =>
  findStandardModel(modelOptions)?.value ||
  modelOptions.find((option) => !option.deprecated)?.value ||
  null

/**
 * The scorecard's eligibility prompt out of a prompt list.
 *
 * @param {object[]} prompts - prompts to search, live or pristine
 * @param {number} eligibilityPromptId - the scorecard config's eligibility_prompt_id, if it has one
 */
export const findEligibilityPrompt = (prompts = [], eligibilityPromptId) =>
  prompts.find(
    (prompt) =>
      (eligibilityPromptId && prompt.id === eligibilityPromptId) || prompt.scid === 'eligibility'
  )

/**
 * The settings form values a given eligibility prompt loads as.
 *
 * @param {object} prompt - an eligibility prompt, or nothing where the scorecard has none yet
 */
export const getEligibilityPromptValues = (prompt) => ({
  user_prompt: prompt?.user_prompt || '',
  first_x_minutes: prompt?.first_x_minutes || '',
  last_x_minutes: prompt?.last_x_minutes || '',
  side: prompt?.side || 'both',
  include_timestamps: getStringValueFromBoolean(prompt?.include_timestamps, 'false'),
  silence_threshold_seconds: prompt?.silence_threshold_seconds || '',
  llm_model_id: prompt?.llm_model_id || null,
})

/**
 * The prompt fields a settings save writes back, out of the form's values.
 *
 * The one place the settings form's hold on the eligibility prompt is spelled out: the save spreads this, and
 * `eligibilityPromptHasChanges` diffs it. A field added here is written and re-arms the deprecated-model block in
 * the same edit, so the two can't drift into writing something the block doesn't watch. `system_prompt` and
 * `max_response_tokens` are deliberately absent — the form reads them for prompt testing but never saves them.
 *
 * @param {object} values - the settings form values
 */
export const getEligibilityPromptPayload = (values) => ({
  user_prompt: values.user_prompt || '',
  first_x_minutes: values.first_x_minutes || null,
  last_x_minutes: values.last_x_minutes || null,
  side: values.side,
  include_timestamps: values.include_timestamps === 'true',
  silence_threshold_seconds: values.silence_threshold_seconds || null,
  llm_model_id: values.llm_model_id || null,
})

/**
 * Has the form edited the eligibility prompt away from the version the API last gave us?
 *
 * Both sides are run through the form-load and save-payload mappings, so only a real edit shows: a prompt the API
 * returns with a null `side` loads as 'both' and saves as 'both', and diffing raw against saved would read the
 * form's own default as an edit. The save leans on this too — where it reports no change the API's own prompt is
 * written back untouched, so the scorecard save skips the POST that a materialized default would have triggered.
 *
 * @param {object} pristinePrompt - the eligibility prompt as the API last returned it
 * @param {object} values - the settings form values
 */
export const eligibilityPromptHasChanges = ({ pristinePrompt, values }) =>
  !isEqual(
    getEligibilityPromptPayload(values),
    getEligibilityPromptPayload(getEligibilityPromptValues(pristinePrompt))
  )

export const updateSectionNameHelper = (scorecardConfig, sectionToUpdate) => {
  return {
    ...scorecardConfig,
    sections: scorecardConfig.sections.map((section) =>
      section.ssid === sectionToUpdate.ssid ? sectionToUpdate : section
    ),
  }
}

export const updateSectionSettingsHelper = (scorecardConfig, sectionToUpdate, updatedSettings) => {
  return {
    ...scorecardConfig,
    sections: scorecardConfig.sections.map((section) => {
      if (section.ssid === sectionToUpdate.ssid) {
        const sectionWithNewSettings = cloneDeep(section)
        sectionWithNewSettings.measures = toArray(sectionWithNewSettings.measures)
        sectionWithNewSettings.measures[0] = {
          ...sectionWithNewSettings.measures[0],
          ...updatedSettings,
        }

        return sectionWithNewSettings
      }

      return section
    }),
  }
}

export const removeSectionHelper = (scorecardConfig, sectionToRemove) => {
  const filteredSections = scorecardConfig.sections.filter(
    (section) => section.ssid !== sectionToRemove.ssid
  )
  if (isEmpty(filteredSections)) {
    filteredSections.push(addDefaultSectionHelper())
  }

  return { ...scorecardConfig, sections: filteredSections }
}

export const createCriteriaHelper = (scorecardConfig, criteriaToAdd) => {
  return {
    ...scorecardConfig,
    sections: scorecardConfig.sections.map((section) => {
      if (section.ssid === criteriaToAdd.ssid) {
        const sectionWithNewCriteria = cloneDeep(section)
        const newCriteria = getFilteredCriteria(criteriaToAdd)
        sectionWithNewCriteria.measures = toArray(sectionWithNewCriteria.measures)
        sectionWithNewCriteria.measures[0].criteria.push(newCriteria)

        return sectionWithNewCriteria
      }
      return section
    }),
  }
}

export const updateCriteriaHelper = (scorecardConfig, criteriaToUpdate) => {
  return {
    ...scorecardConfig,
    sections: scorecardConfig.sections.map((section) => ({
      ...section,
      measures: section.measures.map((measure) => ({
        ...measure,
        criteria: measure.criteria.map((criteria) =>
          criteria.scid === criteriaToUpdate.scid ? criteriaToUpdate : criteria
        ),
      })),
    })),
  }
}

export const removeCriteriaHelper = (scorecardConfig, criteriaToRemove) => {
  return {
    ...scorecardConfig,
    sections: scorecardConfig.sections.map((section) => ({
      ...section,
      measures: section.measures.map((measure) => ({
        ...measure,
        criteria: measure.criteria.filter((criteria) => criteria.scid !== criteriaToRemove.scid),
      })),
    })),
  }
}
